<template>
  <div class="app-container">
    <Header :show-scroll-progress="true" />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <BlogHeader
          :title="title"
          :subheading="subheading"
          :date="date"
          :author="author"
          :author-avatar="authorAvatar"
          :reading-time="readingTime"
          :hero-image="heroImage"
        />

        <div class="event-overwatch-blog">
          <section class="blog-section">
            <p>
              Event work is a strange mix of adrenaline, pressure, and constant
              improvisation. Anyone who has ever stood behind the scenes of a
              large festival, marathon, political rally, or city parade knows
              how chaotic things can get. The director is juggling five
              conversations at once, the security team is dealing with
              bottlenecks that were not supposed to exist, the traffic
              coordinator is on a different radio channel entirely, and sponsors
              are circling around hoping for content they can use. Everyone is
              operating in fragments. Nobody has the full picture. And that is
              exactly why aerial overwatch has quietly become one of the most
              valuable tools an event manager can bring into their workflow.
            </p>
            <p>
              When a drone provides a live overhead feed, the event suddenly
              becomes legible. You give the director something they never have
              enough of: clarity. You give the security team their missing
              angles. You give the broadcast crew reference material. You give
              the city the assurance that critical zones are being monitored
              without relying solely on ground teams. The drone becomes a shared
              vantage point that everyone can anchor to, which is something
              events rarely get.
            </p>
            <p>
              What is interesting is how predictable the problems are. Blind
              spots around entrances. People clustering around a particular
              vendor. A last-minute VIP change in movement. Traffic backups that
              ripple across entire blocks. These are not surprises to anyone who
              runs events. What is surprising is how preventable most of them
              are once everyone can see the same live overhead view.
            </p>
          </section>

          <section class="blog-section">
            <h2>The multi-team problem</h2>
            <p>
              Events usually involve at least four groups who all want different
              information. Security wants choke points. Traffic wants flow
              lines. Broadcast wants anything visually interesting. Sponsors
              want any moment where their placement appears. They are connected,
              but not coordinated. When each team tries to solve their part of
              the event without a unified view, the result is messy decision
              making and slower response times.
            </p>
            <p>
              A live drone feed cuts through this. It becomes a neutral, shared
              source of truth. Not a camera angle for content. A tool for
              coordination. And unlike CCTV, it moves wherever the problem
              moves.
            </p>
          </section>

          <section class="blog-section">
            <h2>Finding the signal inside the noise</h2>
            <p>
              Most operators are surprised the first time they fly overwatch at
              a big event. You think the client wants pretty footage. What they
              actually want is insight. They need to be able to say things like:
            </p>
            <ul class="insights-list">
              <li>There is a build-up at Gate C</li>
              <li>That exit path is starting to thicken</li>
              <li>The VIP route looks clear</li>
              <li>
                The crowd near the main stage is beginning to curve inward
              </li>
            </ul>
            <p>
              They need to capture incidents in a way that can be bookmarked and
              revisited later without searching through hours of video. They
              need reference material for safety reports. They need proof that
              protocols were followed. And they need all of this while the event
              is still happening, not two days later when the opportunity to fix
              something is already gone.
            </p>
          </section>

          <section class="blog-section">
            <h2>Where the real value appears</h2>
            <p>
              The best operators are the ones who realize that the drone is not
              just an eye in the sky. It is an early warning system. When you
              can see density changes forming before they become visible from
              the ground, you give the event team time. Time to adjust gates.
              Time to redirect flows. Time to prevent small issues from becoming
              actual incidents.
            </p>
            <p>
              This is the moment when your service stops being optional. It
              becomes a line item that feels essential. Many events will pay
              serious money for that kind of insurance. It is common to see
              operators add a dedicated Overwatch package that runs between one
              and three thousand dollars per day because what they provide is
              not footage. It is risk reduction.
            </p>
          </section>

          <section class="blog-section">
            <h2>What turns a good feed into a great one</h2>
            <p>
              Events move fast. The feed should keep up. Skyhost helps here
              because it lets you invite the exact people who need access, all
              in separate roles if needed. Security can view privately. Sponsors
              can be given a cleaned up link. The director sees everything in
              real time.
            </p>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>Becoming indispensable rather than optional</h2>
            <p>
              If you are offering drone services for events, you are competing
              in a world where budgets shift constantly and every vendor has to
              prove they deserve the slot. The operators who win consistently
              are the ones who move beyond "we can capture cool shots" and step
              into "we help you run this event smarter and safer."
            </p>
            <p>
              Aerial overwatch gives you a seat at the operations table instead
              of the content table. It puts you in the room with decision
              makers, not just media teams. And once those teams see what
              real-time aerial visibility does for them, they start asking for
              you by name.
            </p>
            <p>
              Ready to transform your event services? Get started with
              <a href="https://skyhost.ai" target="_blank" class="blog-link"
                >Skyhost</a
              >
              today.
            </p>
          </section>
        </div>

        <BlogFooter
          :tags="tags"
          :title="title"
          :current-page-url="currentPageUrl"
          :related-blogs="relatedBlogs"
          @navigate-to-blog="navigateToBlog"
          @navigate-to-blogs="navigateToBlogs"
        />
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import BlogHeader from "@/components/organisms/BlogHeader.vue";
import BlogFooter from "@/components/organisms/BlogFooter.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "EventAerialOverwatchBlog",
  components: {
    Header,
    Footer,
    BlogHeader,
    BlogFooter,
  },
  data() {
    const blogId = "event-aerial-overwatch";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      subheading: blog.subheading,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
    readingTime() {
      const wordCount =
        this.title.split(" ").length +
        (this.excerpt?.split(" ").length || 0) +
        2000;
      return Math.ceil(wordCount / 200);
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    const allOtherBlogs = Object.values(blogsRegistry).filter(
      (blog) => blog.id !== this.id
    );

    // First, try to get blogs with matching tags
    const matchingTagBlogs = allOtherBlogs.filter((blog) =>
      blog.tags.some((tag) => this.tags.includes(tag))
    );

    // If we have matching tag blogs, use them, otherwise use any other blogs
    const relatedBlogs =
      matchingTagBlogs.length > 0 ? matchingTagBlogs : allOtherBlogs;

    // Take up to 2 blogs
    this.relatedBlogs = relatedBlogs.slice(0, 2);
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}
.event-overwatch-blog {
  font-family: "Charter", "Georgia", "Cambria", serif;
  line-height: 1.8;
  color: #333;
  font-size: 21px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

h3 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 600;
  letter-spacing: -0.01em;
}

p {
  margin-bottom: 1.5rem;
  font-size: 21px;
  line-height: 1.8;
  font-family: "Charter", "Georgia", "Cambria", serif;
}

.insights-list {
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
  font-family: "Charter", "Georgia", "Cambria", serif;
  font-size: 21px;
}

.insights-list li {
  margin-bottom: 0.8rem;
  line-height: 1.8;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .web-blog-content {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.75rem;
  }
}
</style>
